import find from 'lodash/find';

import {
  HEATMAP_NORMALIZED_WEIGHT_PROPERTY,
  HEATMAP_WEIGHT_PROPERTY,
} from './constants';

const DEFAULT_UNWEIGHTED_VALUE = 1;
const DEFAULT_MISSING_WEIGHT_VALUE = 0;
const MIN_POSITIVE_VISUAL_WEIGHT = 0.18;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getStatisticValue = (feature, heatmapWeightField) => {
  const statistics = feature.properties && feature.properties.data ? feature.properties.data : [];
  const statistic = statistics.find(({ name }) => name === heatmapWeightField);
  const value = statistic ? Number(statistic.value) : DEFAULT_MISSING_WEIGHT_VALUE;

  return Number.isFinite(value) ? value : DEFAULT_MISSING_WEIGHT_VALUE;
};

const getWeightRange = (aggregatableFields, heatmapWeightField) => {
  if (!heatmapWeightField) {
    return null;
  }

  const range = find(aggregatableFields, { name: heatmapWeightField });

  if (!range) {
    return null;
  }

  const min = Number(range.min);
  const max = Number(range.max);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return { min, max };
};

const normalizeWeight = (value, range) => {
  if (!range) {
    return DEFAULT_UNWEIGHTED_VALUE;
  }

  const safeValue = Math.max(value, 0);

  if (range.max <= range.min) {
    return safeValue > 0 ? DEFAULT_UNWEIGHTED_VALUE : DEFAULT_MISSING_WEIGHT_VALUE;
  }

  const normalized = (safeValue - range.min) / (range.max - range.min);

  return clamp(normalized, DEFAULT_MISSING_WEIGHT_VALUE, DEFAULT_UNWEIGHTED_VALUE);
};

const toVisualWeight = (normalizedWeight) => {
  if (normalizedWeight <= DEFAULT_MISSING_WEIGHT_VALUE) {
    return DEFAULT_MISSING_WEIGHT_VALUE;
  }

  const compressedWeight = Math.sqrt(normalizedWeight);

  return MIN_POSITIVE_VISUAL_WEIGHT
    + (compressedWeight * (DEFAULT_UNWEIGHTED_VALUE - MIN_POSITIVE_VISUAL_WEIGHT));
};

export const prepareHeatmapData = ({
  datapointsGeojson,
  heatmapWeightField,
  aggregatableFields,
}) => {
  if (!datapointsGeojson) {
    return null;
  }

  const hasWeightField = Boolean(heatmapWeightField);
  const weightRange = getWeightRange(aggregatableFields, heatmapWeightField);
  const features = datapointsGeojson.features.map((feature) => {
    const rawWeight = hasWeightField
      ? getStatisticValue(feature, heatmapWeightField)
      : DEFAULT_UNWEIGHTED_VALUE;
    const normalizedWeight = hasWeightField
      ? normalizeWeight(rawWeight, weightRange)
      : DEFAULT_UNWEIGHTED_VALUE;

    return {
      ...feature,
      properties: {
        ...feature.properties,
        [HEATMAP_WEIGHT_PROPERTY]: rawWeight,
        [HEATMAP_NORMALIZED_WEIGHT_PROPERTY]: toVisualWeight(normalizedWeight),
      },
    };
  });

  return {
    ...datapointsGeojson,
    features,
  };
};

export const heatmapDataUtils = {
  getStatisticValue,
  getWeightRange,
  normalizeWeight,
  toVisualWeight,
};
